import { Box, Typography } from '@mui/material';
import Radio from 'components/Common/Radio';
import Input from 'components/Common/Input';
import { shrinkLabel, StepFormProps, YES_NO_OPTIONS } from './campaignFormShared';

type BoolField =
  | 'subscriber_opt_in'
  | 'subscriber_opt_out'
  | 'subscriber_help'
  | 'number_pool'
  | 'direct_lending'
  | 'embedded_link'
  | 'embedded_phone'
  | 'age_gated';

function Question({
  name,
  label,
  children,
  control,
  lockBooleans
}: {
  name: BoolField;
  label: string;
  children?: React.ReactNode;
  control: StepFormProps['control'];
  lockBooleans?: boolean;
}) {
  return (
    <Box mb={3}>
      <Typography fontSize={14} fontWeight={500} color='text.primary' mb={0.5}>
        {label}
      </Typography>
      <Radio
        name={name}
        control={control}
        options={YES_NO_OPTIONS}
        row
        disabled={lockBooleans}
        formControlLabelSx={{ mr: 2, ml: 0, my: 0 }}
      />
      {children}
    </Box>
  );
}

export default function AttributesStep({ control, watch, lockBooleans }: StepFormProps) {
  const optIn = watch('subscriber_opt_in');
  const optOut = watch('subscriber_opt_out');
  const help = watch('subscriber_help');

  return (
    <Box>
      <Typography fontSize={16} fontWeight={600} color='text.primary' mb={2.5}>
        Campaign Attributes
      </Typography>

      <Question
        name='subscriber_opt_in'
        control={control}
        lockBooleans={lockBooleans}
        label='Are you collecting and processing consumer opt-in?'
      >
        {optIn ? (
          <>
            <Input
              name='optin_keywords'
              control={control}
              label='Subscriber opt-in keywords'
              placeholder='Enter Here'
              fullWidth
              InputLabelProps={shrinkLabel}
              helperText='The word(s) a customer can text to opt in (e.g. START, YES).'
            />
            <Box mt={1.5}>
              <Input
                name='optin_message'
                control={control}
                label='Subscriber opt-in message'
                placeholder='Enter Here'
                fullWidth
                multiline
                minRows={2}
                InputLabelProps={shrinkLabel}
                helperText='The confirmation message sent when a customer agrees to receive texts.'
              />
            </Box>
          </>
        ) : null}
      </Question>

      <Question
        name='subscriber_opt_out'
        control={control}
        lockBooleans={lockBooleans}
        label='Are you collecting and processing consumer opt-out?'
      >
        {optOut ? (
          <>
            <Input
              name='optout_keywords'
              control={control}
              label='Subscriber opt-out keywords'
              fullWidth
              InputLabelProps={shrinkLabel}
              helperText='The word(s) a customer can text to unsubscribe. Defaults to STOP.'
            />
            <Box mt={1.5}>
              <Input
                name='optout_message'
                control={control}
                label='Subscriber opt-out message'
                required
                fullWidth
                multiline
                minRows={2}
                InputLabelProps={shrinkLabel}
                rules={{ required: 'Opt-out message is required' }}
                helperText='The confirmation message sent when a customer unsubscribes.'
              />
            </Box>
          </>
        ) : null}
      </Question>

      <Question
        name='subscriber_help'
        control={control}
        lockBooleans={lockBooleans}
        label='Have you implemented a response to the HELP keyword informing customers of how they can contact the message sender?'
      >
        {help ? (
          <>
            <Input
              name='help_keywords'
              control={control}
              label='Subscriber help keywords'
              fullWidth
              InputLabelProps={shrinkLabel}
              helperText='The word(s) a customer can text to request help. Defaults to HELP.'
            />
            <Box mt={1.5}>
              <Input
                name='help_message'
                control={control}
                label='Subscriber help message'
                required
                fullWidth
                multiline
                minRows={2}
                InputLabelProps={shrinkLabel}
                rules={{ required: 'Help message is required' }}
                helperText='The message sent when a customer asks for help.'
              />
            </Box>
          </>
        ) : null}
      </Question>

      <Question
        name='direct_lending'
        control={control}
        lockBooleans={lockBooleans}
        label='Will the campaign include content related to direct lending or other loan arrangements?'
      />
      <Question
        name='embedded_link'
        control={control}
        lockBooleans={lockBooleans}
        label='Are you using an embedded link of any kind?'
      />
      <Question
        name='embedded_phone'
        control={control}
        lockBooleans={lockBooleans}
        label='Are you using any embedded phone number beyond the contact number in the HELP response?'
      />
      <Question
        name='age_gated'
        control={control}
        lockBooleans={lockBooleans}
        label='Will the campaign include any age-gated content as defined by carrier and CTIA guidelines?'
      />
    </Box>
  );
}
